import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, message, Upload, Modal, Spin } from 'antd';
import {
	PaperClipOutlined,
	EyeOutlined,
	DeleteOutlined,
	CloudDownloadOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getSignedUrl } from 'store/commonSlice';

const FileListContainer = styled.div`
	margin-top: 12px;
`;

const FileItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	background: #f5f5f5;
	border-radius: 4px;
	margin-bottom: 8px;
`;

const FileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1;
`;

const FileName = styled.span`
	font-size: 14px;
	color: #333;
	margin-left: 8px;
`;

const FileSize = styled.span`
	font-size: 12px;
	color: #666;
	margin-left: 8px;
`;

const FileActions = styled.div`
	display: flex;
	gap: 8px;
`;

const ActionButton = styled(Button)`
	padding: 4px 8px;
	height: auto;
	border: none;
	box-shadow: none;

	&:hover {
		background: #e6f7ff;
	}
`;

const AttachButton = styled(Button)`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 8px;
`;

function TdsFileUpload({ files = [], onFilesChange, maxFiles = 10, entity = 'vendor_bill' }) {
	const dispatch = useDispatch();
	const [uploading, setUploading] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewFile, setPreviewFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);

	const handleFileUpload = async (file) => {
		if (!file) {
			message.error('Please select a file');
			return false;
		}

		// File validation
		const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
		const maxSizeMB = 5;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, PNG, JPG, JPEG files!');
			return false;
		}

		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		// Check max files limit
		if (files.length >= maxFiles) {
			message.error(`Maximum ${maxFiles} files allowed`);
			return false;
		}

		setUploading(true);

		try {
			// Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity,
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || (!fileData?.filename && !fileData?.key)) {
				throw new Error('Failed to get signed URL');
			}

			// Upload file to S3
			const uploadResponse = await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			if (!uploadResponse.ok) {
				throw new Error('Failed to upload file to S3');
			}

			// Use 'filename' from response for image_keys payload (UUID-prefixed filename)
			// The 'filename' is the full S3 key that should be used in image_keys payload
			const s3Filename = fileData.filename || fileData.key;

			// Add file to list
			const newFile = {
				id: Date.now(),
				name: file.name,
				size: file.size,
				type: file.type,
				key: fileData.key || fileData.filename, // Store original key for reference
				filename: s3Filename, // Store filename (UUID-prefixed) for image_keys payload
				url: fileData.url || s3Filename, // Store signed URL or filename for retrieval
			};

			onFilesChange([...files, newFile]);
			message.success('File uploaded successfully');
			return true;
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload file');
			return false;
		} finally {
			setUploading(false);
		}
	};

	const handleFileRemove = (fileId) => {
		const updatedFiles = files.filter((f) => f.id !== fileId);
		onFilesChange(updatedFiles);
		message.success('File removed');
	};

	const handleFileView = async (file) => {
		if (!file) {
			message.error('No file provided');
			return;
		}

		setPreviewLoading(true);
		setPreviewFile(file);
		setPreviewVisible(true);
		setPreviewUrl(null);

		try {
			// Extract S3 key from file object
			// Priority: filename (UUID-prefixed) > key > url
			const s3Key = file.filename || file.key || file.url;

			if (!s3Key) {
				message.error('File key not found');
				setPreviewLoading(false);
				return;
			}

			// Get signed URL for viewing
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity,
						keys: [s3Key],
					},
				})
			);

			// Extract signed URL from response
			const signedUrl =
				(signedUrlResponse &&
					signedUrlResponse.status === 'success' &&
					signedUrlResponse.data?.[0]?.url) ||
				(signedUrlResponse?.data &&
					Array.isArray(signedUrlResponse.data) &&
					signedUrlResponse.data.length > 0 &&
					signedUrlResponse.data[0]?.url)
					? signedUrlResponse.data[0].url
					: null;

			if (signedUrl) {
				setPreviewUrl(signedUrl);
			} else {
				message.error('Failed to load document preview');
				setPreviewUrl(null);
			}
		} catch (error) {
			console.error('Error getting signed URL for view:', error);
			message.error('Failed to load document preview');
			setPreviewUrl(null);
		} finally {
			setPreviewLoading(false);
		}
	};

	const handleFileDownload = async (file) => {
		try {
			// Use the same key priority as view (filename = full UUID-prefixed S3 key);
			// file.key can be a shorter reference key that doesn't exist in S3.
			const s3Key = file.filename || file.key || file.url;
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'download',
					data: {
						entity,
						keys: [s3Key],
					},
				})
			);

			const downloadUrl = signedUrlResponse?.data?.[0]?.url;
			if (downloadUrl) {
				window.open(downloadUrl, '_blank');
			} else {
				message.error('Failed to get download URL');
			}
		} catch (error) {
			console.error('Download error:', error);
			message.error('Failed to download file');
		}
	};

	const renderPreviewContent = () => {
		if (previewLoading) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<Spin size="large" tip="Loading preview..." />
				</div>
			);
		}

		if (!previewUrl) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<p>Failed to load preview</p>
					<Button onClick={() => handleFileView(previewFile)}>Retry</Button>
				</div>
			);
		}

		if (previewFile?.type?.startsWith('image/')) {
			return (
				<img
					src={previewUrl}
					alt={previewFile.name}
					style={{
						width: '100%',
						height: 'auto',
						maxHeight: '70vh',
						objectFit: 'contain',
					}}
					onError={() => {
						message.error('Failed to load image');
					}}
				/>
			);
		}

		if (previewFile?.type === 'application/pdf') {
			return (
				<iframe
					src={previewUrl}
					title={previewFile.name}
					style={{ width: '100%', height: '600px', border: 'none' }}
				/>
			);
		}

		return (
			<div style={{ textAlign: 'center', padding: '40px' }}>
				<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
	};

	const uploadProps = {
		beforeUpload: (file) => {
			handleFileUpload(file);
			return false; // Prevent default upload
		},
		accept: '.pdf,.jpg,.jpeg,.png',
		showUploadList: false,
		multiple: true,
	};

	return (
		<>
			<Upload {...uploadProps}>
				<AttachButton
					type="default"
					icon={<PaperClipOutlined />}
					loading={uploading}
					disabled={uploading || files.length >= maxFiles}
				>
					Attach
				</AttachButton>
			</Upload>

			{files.length > 0 && (
				<FileListContainer>
					{files.map((file) => (
						<FileItem key={file.id || file.key}>
							<FileInfo>
								<PaperClipOutlined />
								<FileName>{file.name}</FileName>
								<FileSize>{formatFileSize(file.size)}</FileSize>
							</FileInfo>
							<FileActions>
								<ActionButton
									type="text"
									icon={<EyeOutlined />}
									onClick={() => handleFileView(file)}
									title="View"
								/>
								<ActionButton
									type="text"
									icon={<CloudDownloadOutlined />}
									onClick={() => handleFileDownload(file)}
									title="Download"
								/>
								<ActionButton
									type="text"
									icon={<DeleteOutlined />}
									onClick={() => handleFileRemove(file.id || file.key)}
									title="Delete"
									danger
								/>
							</FileActions>
						</FileItem>
					))}
				</FileListContainer>
			)}

			<Modal
				visible={previewVisible}
				title={previewFile ? `File Preview - ${previewFile.name}` : 'File Preview'}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewFile(null);
					setPreviewUrl(null);
				}}
				width={800}
				centered
			>
				{renderPreviewContent()}
			</Modal>
		</>
	);
}

TdsFileUpload.propTypes = {
	files: PropTypes.array,
	onFilesChange: PropTypes.func.isRequired,
	maxFiles: PropTypes.number,
	entity: PropTypes.string,
	useV2API: PropTypes.bool,
};

export default TdsFileUpload;
